import { useEffect, useMemo, useState } from 'react';
import { api, HttpError } from '../api/client';
import type { QaPendingResponse, QaVerdict, QaVerdictResult } from '../api/types';
import { useStoreSlice } from '../store/storeRegistry';
import { AppHeader } from '../components/AppHeader';
import { StoreRunButton } from '../components/StoreRunButton';
import { QaReviewGallery } from '../components/QaReviewGallery';
import { RendersGallery } from '../components/RendersGallery';

function errDetail(err: unknown): string {
  return err instanceof HttpError ? err.detail : err instanceof Error ? err.message : 'unknown error';
}

export function CutoutQaTab() {
  const [view, setView] = useState<'review' | 'all' | 'batch'>('review');
  const [pending, setPending] = useState<QaPendingResponse | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [verdictsText, setVerdictsText] = useState('');
  const [result, setResult] = useState<QaVerdictResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const { selectedStoreId } = useStoreSlice();

  // W-C [UX-7]: a failed pending fetch used to render as a serene "0 pending".
  useEffect(() => {
    setPendingError(null);
    api.getQaPending(50, selectedStoreId)
      .then(setPending)
      .catch((err) => setPendingError(errDetail(err)));
  }, [selectedStoreId]);

  const pendingCount = pending?.items.length ?? 0;

  const batchPrompt = pending
    ? `${pending.rubric}\n\n` +
      pending.items
        .map(
          (it, i) =>
            `${i + 1}. product_id=${it.product_id}\n   SOURCE: ${it.source_image_url}\n` +
            `   AD: ${it.ad_image_url}\n   CUTOUT: ${it.cutout_image_url ?? '(n/a)'}`,
        )
        .join('\n')
    : '';

  // Live validation: parse the verdicts textarea on every change. `null` means
  // "nothing to validate yet" (empty input); a string is the error message.
  const parseError = useMemo<string | null>(() => {
    const trimmed = verdictsText.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) return 'Invalid JSON — expected an array of verdicts';
      return null;
    } catch {
      return 'Invalid JSON';
    }
  }, [verdictsText]);

  const canSubmit = verdictsText.trim().length > 0 && parseError === null && !submitting;

  async function submit() {
    if (!canSubmit) return;
    let parsed: QaVerdict[];
    try {
      parsed = JSON.parse(verdictsText);
    } catch {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await api.submitQaVerdicts(parsed, selectedStoreId);
      setResult(res);
      setVerdictsText('');
      api.getQaPending(50, selectedStoreId)
        .then(setPending)
        .catch((err) => setPendingError(errDetail(err)));
    } catch (err) {
      // W-C [UX-7]: a failed submit silently looked like it worked.
      setSubmitError(`Submitting verdicts failed: ${errDetail(err)}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function copyBatch() {
    try {
      await navigator.clipboard.writeText(batchPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard may be unavailable; the textarea is selectable as a fallback */
    }
  }

  return (
    <div className="min-h-screen bg-ff-ivory relative">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" aria-hidden="true" />

      <AppHeader
        active="qa"
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            {pendingError && (
              <span
                className="ff-chip border border-ff-danger/30 bg-ff-danger-soft text-ff-danger"
                title={pendingError}
                data-testid="qa-pending-error"
              >
                pending: unavailable
              </span>
            )}
            <StoreRunButton storeId={selectedStoreId} />
          </div>
        )}
      />

      <main className="relative mx-auto max-w-[1800px] px-5 pb-24 pt-10 animate-rise sm:px-8 sm:pt-12">
        <div className="mb-8 border-b border-ff-line pb-9 sm:mb-10 sm:pb-12">
          <p className="ff-kicker mb-4">Quality workspace</p>
          <h1 className="ff-page-title text-[clamp(3.25rem,7vw,5.25rem)] leading-[0.92]">
            A second
            <span className="block text-ff-orange">pair of eyes.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-ff-mute">
            Review, decide, and remediate cutouts — filter the queue, run a fresh cut,
            or hand a batch to the assistant. Passing cutouts are recorded so the
            pipeline trusts them on every future run.
          </p>
        </div>

        <div className="ff-segment mb-8 max-w-full overflow-x-auto">
          {(['review', 'all', 'batch'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={
                'whitespace-nowrap px-4 py-2 text-xs font-bold uppercase tracking-widish rounded-full transition-colors ' +
                (view === v
                  ? 'ff-segment-active'
                  : 'text-ff-mute hover:text-ff-ink')
              }
            >
              {v === 'review' ? 'Review' : v === 'all' ? 'All renders' : 'Batch tools'}
            </button>
          ))}
        </div>

        {view === 'review' && <QaReviewGallery />}

        {view === 'all' && <RendersGallery storeId={selectedStoreId} />}

        {view === 'batch' && (
        <>
        {/* Step 1 — generate review batch */}
        <section className="ff-card mb-8 p-5 sm:p-7">
          <div className="flex items-baseline gap-3 mb-1">
            <span className="font-display text-2xl font-bold leading-none text-ff-orange">01</span>
            <h2 className="font-display text-xl font-bold text-ff-ink">Generate review batch</h2>
          </div>
          <p className="text-sm text-ff-mute mb-4 pl-9">
            Paste this batch to the AI / chat; it returns JSON verdicts.
          </p>

          {pendingCount === 0 ? (
            <div className="ml-0 rounded-xl border border-dashed border-ff-line bg-ff-paper px-6 py-10 text-center sm:ml-9 sm:px-8">
              <p className="font-display text-lg font-bold text-ff-ink">All clear.</p>
              <p className="mt-1 text-sm text-ff-mute">
                Nothing is waiting for QA right now.
              </p>
            </div>
          ) : (
            <div className="ml-0 sm:ml-9">
              <textarea
                readOnly
                data-testid="qa-batch"
                rows={10}
                value={batchPrompt}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full rounded-xl border border-ff-sand bg-ff-paper/60 p-4 font-mono text-[12px] leading-relaxed text-ff-ink focus:outline-none focus:ring-2 focus:ring-ff-forest/20"
              />
              <div className="mt-3 flex items-center gap-3">
                <button className="ff-btn-ghost" onClick={copyBatch}>
                  {copied ? 'Copied' : 'Copy batch'}
                </button>
                <span className="font-mono text-[11px] uppercase tracking-widish text-ff-mute">
                  {pendingCount} item{pendingCount === 1 ? '' : 's'} in this batch
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Step 2 — paste verdicts */}
        <section className="ff-card p-5 sm:p-7">
          <div className="flex items-baseline gap-3 mb-1">
            <span className="font-display text-2xl font-bold leading-none text-ff-orange">02</span>
            <h2 className="font-display text-xl font-bold text-ff-ink">Paste the AI's verdicts</h2>
          </div>
          <p className="text-sm text-ff-mute mb-4 pl-9">
            A JSON array of <span className="font-mono text-[12px]">{'{ product_id, verdict }'}</span>{' '}
            objects. We validate as you type.
          </p>

          <div className="ml-0 sm:ml-9">
            <textarea
              data-testid="qa-verdicts-input"
              rows={7}
              value={verdictsText}
              onChange={(e) => setVerdictsText(e.target.value)}
              placeholder='[{"product_id": "…", "verdict": "pass"}]'
              aria-invalid={parseError !== null}
              className={
                'w-full rounded-xl border bg-white p-4 font-mono text-[12px] leading-relaxed text-ff-ink placeholder:text-ff-mute/60 focus:outline-none focus:ring-2 transition-colors ' +
                (parseError
                  ? 'border-ff-danger/70 focus:ring-ff-danger/20'
                  : 'border-ff-sand focus:ring-ff-forest/20 focus:border-ff-forest')
              }
            />

            {parseError && (
              <p className="mt-2 flex items-center gap-2 text-sm text-ff-danger" role="alert">
                <span aria-hidden="true">●</span>
                {parseError}
              </p>
            )}

            {submitError && (
              <p className="mt-2 flex items-center gap-2 text-sm text-ff-danger" role="alert">
                <span aria-hidden="true">●</span>
                {submitError}
              </p>
            )}

            <div className="mt-4 flex items-center gap-3">
              <button
                data-testid="qa-submit"
                className="ff-btn-primary"
                onClick={submit}
                disabled={!canSubmit}
              >
                {submitting ? 'Submitting…' : 'Submit verdicts'}
              </button>

              {result && (
                <div
                  data-testid="qa-result"
                  className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widish"
                >
                  <span className="ff-chip bg-ff-mint/50 text-ff-forest">
                    {result.written} written
                  </span>
                  <span className="ff-chip bg-ff-mint/30 text-ff-forest">
                    {result.passed} passed
                  </span>
                  <span className="ff-chip bg-ff-danger-soft text-ff-danger">
                    {result.failed} failed
                  </span>
                </div>
              )}
            </div>
          </div>
        </section>
        </>
        )}
      </main>
    </div>
  );
}
